import {
  Heading,
  Text,
  Button,
  Box,
  Icon,
  Image,
  IconButton,
  theme,
} from '@everlywell/ui-kit';
import { CheckCircle } from '@phosphor-icons/react';
import DynamicIcon from 'components/DynamicIcon';
import EmailField from 'components/FormFields/EmailField';
import { useAsyncProgramPPQContext } from 'contexts/AsyncProgramPPQ/AsyncProgramPPQContext';
import { navigate } from 'gatsby';
import React, { useEffect, SyntheticEvent } from 'react';
import { useFormContext } from 'react-hook-form';
import analytics from 'utils/analytics';
import { sendNotificationEvent } from 'utils/api/subscriptionsApi';
import { ANALYTICS } from 'utils/constants/analytics';
import { showFlash } from 'utils/domHelpers';

import { QuizIdsEnum } from '../../constants';
import {
  getEligibleContent,
  getMobileImageByProgram,
  getProgramSession,
} from './utils';

export type YouAreEligibleProps = {
  quizId: QuizIdsEnum;
};

export type PrePurchaseQuizEmailData = {
  email: string;
};

/**
 * This component adds the You Are Eligible screen the pre-purchase quiz modal.
 */

const MOBILE_EMAIL_HEIGHT = 330;
const DESKTOP_EMAIL_HEIGHT = 250;

const buttonPadding = [
  `${theme.space[2]} ${theme.space[4]}`,
  `${theme.space[4]} ${theme.space[6]}`,
];

// TODO:
// - move content to contentful and fetch it from there
function YouAreEligible({ quizId }: YouAreEligibleProps): JSX.Element {
  const { toggleModal, resetState, personalData, questionsAndAnswers } =
    useAsyncProgramPPQContext();
  const { register, watch, handleSubmit, formState, errors } = useFormContext();
  const { isSubmitting, isSubmitted } = formState;

  const handleModalClose = () => {
    toggleModal(false);
    resetState();
  };

  const emailValue = watch('email');

  useEffect(() => {
    analytics.track({
      event: ANALYTICS.EVENTS.VIEWED_COMPONENT,
      data: {
        label: ANALYTICS.ASYNC_PROGRAM_PPQ.LABEL,
        step: ANALYTICS.ASYNC_PROGRAM_PPQ.STEPS.YOU_ARE_ELIGIBLE,
      },
    });
  }, []);

  const trackButtonClick = (trackProps: Record<string, string>) => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: ANALYTICS.ASYNC_PROGRAM_PPQ.LABEL,
        step: ANALYTICS.ASYNC_PROGRAM_PPQ.STEPS.YOU_ARE_ELIGIBLE,
        ...trackProps,
      },
    });
  };

  const onSubmit = async () => {
    // We need to send the data in the shape of [{ question: string; answer: string | string[] }] to the API
    const transformedData = [];

    // Transform personal data
    if (personalData) {
      const personalDataEntries = Object.entries(personalData.personalData);
      const personalDataTransformed = personalDataEntries.map(
        ([key, value]) => ({
          question: key,
          answer: value,
        }),
      );
      transformedData.push(...personalDataTransformed);
    }

    // questionsAndAnswers is already in the correct shape, we just need to push it to the transformedData array
    if (questionsAndAnswers) {
      transformedData.push(...questionsAndAnswers);
    }

    // Now you can use the transformedData to send to your API
    try {
      await sendNotificationEvent({
        event_type: 'newsletter-subscription',
        resource: {
          email: emailValue,
          data_fields: {
            quiz: transformedData,
            newsletter: quizId,
          },
        },
      });

      trackButtonClick({ cta: 'Submit', quizId });
    } catch (error) {
      showFlash(
        'alert-error',
        `We’re sorry! There is an error processing your request at the moment. Please try again later`,
      );
    }
  };

  // Redirect to the program session page
  const handleProgramSession = (e: SyntheticEvent) => {
    e.preventDefault();
    const programSession = getProgramSession(quizId);
    trackButtonClick({ cta: 'Get started', quizId });
    navigate(programSession);
  };

  const { desktopModalImage } = getMobileImageByProgram(quizId);

  return (
    <Box
      alignItems="center"
      backgroundColor="moonlight.base"
      display="flex"
      flexDirection={'column'}
      height="100vh"
      justifyContent={['flex-end', 'center', 'space-between']}
      position="relative"
      textAlign="center"
    >
      <IconButton
        colorScheme="whiteCS"
        size="sm"
        aria-label="close"
        icon={<DynamicIcon icon="X" color="tints.uiGrey" />}
        onClick={handleModalClose}
        width="40px"
        height="40px"
        position="absolute"
        top="20px"
        right={['24px', null, '70px']}
      />
      <Box
        margin={'auto'}
        maxWidth={[null, '600px']}
        paddingX="5"
        zIndex={2}
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={8}
      >
        <Icon
          as={CheckCircle}
          color="moonlight.lighter"
          boxSize="36px"
          weight="fill"
        />
        <Heading
          as="h2"
          color="tints.white"
          fontSize="6xl"
          lineHeight={['1.3', '1.2']}
          size="xl"
        >
          You're eligible for a {getEligibleContent(quizId)} treatment
        </Heading>
        <Button
          colorScheme="whiteCS"
          onClick={handleProgramSession}
          padding={buttonPadding}
          size={['sm']}
          variant="primary"
        >
          Get started
        </Button>
      </Box>

      <Image
        alt="Image of a hexagon pattern and a woman smiling"
        bottom={DESKTOP_EMAIL_HEIGHT}
        display={['none', null, 'block']} // Show this image on desktop. This is a temporary hack until we move the images to Contentful.
        maxWidth="850px"
        position="absolute"
        src={desktopModalImage}
        zIndex={1}
      />
      <Box
        alignItems={'center'}
        backgroundColor="moonlight.wash"
        bottom={0}
        display={'flex'}
        flexDirection={['column', null, 'row']}
        height={[MOBILE_EMAIL_HEIGHT, DESKTOP_EMAIL_HEIGHT]}
        justifyContent={'center'}
        padding={['80px 16px', null, 80]}
        width="100vw"
      >
        <Box
          color="tints.black"
          flex={1}
          maxWidth={456}
          textAlign={['center', null, 'left']}
          paddingRight={['0', '0', '4']}
        >
          <Heading as="h3" marginBottom={['3', '2']}>
            Not ready for a treatment?
          </Heading>
          <Text color="tints.darkGrey" marginBottom={['1', '2']}>
            Enter your email to stay up to date on all of our virtual care
            solutions.
          </Text>
        </Box>
        <Box
          display={isSubmitted ? 'flex' : 'none'}
          flex={1}
          maxWidth={480}
          paddingTop={['8', '8', '0']}
          width={['100%', null, 'auto']}
          flexDirection={['column', null, 'row']}
          alignItems={['center', null, 'flex-start']}
        >
          <Icon
            as={CheckCircle}
            color="moonlight.base"
            boxSize={['20px', '20px', '32px']}
            weight="fill"
          />
          <Text
            color="moonlight.base"
            fontSize={['18', null, '20']}
            fontWeight="medium"
            paddingLeft={['0', '0', '2']}
            paddingTop={['1', '1', '0']}
          >
            Thank you, we will get in contact soon.
          </Text>
        </Box>
        <Box
          display={isSubmitted ? 'none' : 'flex'}
          flex={1}
          maxWidth={480}
          paddingTop={['8', '8', '0']}
          width={['100%', null, 'auto']}
        >
          <EmailField
            register={register}
            placeholder="janesmith@email.com"
            errors={errors}
          />
          <Button
            colorScheme="greenCS"
            height={['44px', '52px']}
            isDisabled={!emailValue}
            isLoading={isSubmitting}
            marginLeft={'4'}
            onClick={handleSubmit(onSubmit)}
            padding={buttonPadding}
            size={['sm']}
            variant="primary"
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default YouAreEligible;
